import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ChevronDown,
  Play,
  Box,
  Code2,
  ShieldCheck,
  Rocket,
  ArrowRightCircle,
  Lock,
  Check,
} from 'lucide-react'
import { useAppStore, APP_MODE_CONFIGS } from '@/store/appStore'
import type { AppMode } from '@/store/appStore'
import { useProjectEntitlementStore } from '@/config/projectEntitlements'
import { isInternalSurface } from '@/config/productSurface'
import { getDemoCompany } from '@/types/demoCompanies'

/**
 * In-place lifecycle switch (tunnel model): can the CURRENT selection survive
 * the target mode's data scope? If yes, the mode switch keeps the user on the
 * page they're on (it re-renders with the new mode's data); if not, we fall
 * back to the home workspace. Mirrors the per-mode scoping in usePortfolioData:
 *   demo        → the picked company's portfolio subtree
 *   sandbox     → exact organizationId match on the picked sandbox org
 *                 (demo subtrees are excluded from sandbox entirely)
 *   development / quality → unscoped, everything survives
 *   production  → exact organizationId match on the active org
 * Conservative on purpose: any doubt → home workspace, never a broken page.
 */
function selectionSurvives(target: AppMode): boolean {
  const { selectedPortfolio, contextSelected, currentCompanyId, activeOrganizationId, appMode } =
    useAppStore.getState()
  if (!contextSelected || !selectedPortfolio) return false
  const orgId = (selectedPortfolio as { organizationId?: string | null }).organizationId ?? null
  switch (target) {
    case 'development':
    case 'quality':
      return true
    case 'demo': {
      if (!currentCompanyId) return true // unscoped demo shows everything
      // Cheap sync proxy for "inside the company subtree": the portfolio's org
      // belongs to the company's castOrg family (org_gtxvs, org_gtxvs_aero, …).
      const castOrg = getDemoCompany(currentCompanyId)?.castOrgId
      if (!castOrg) return false
      const fam = castOrg.replace(/_corp$/, '')
      return !!orgId && (orgId === castOrg || orgId === fam || orgId.startsWith(fam + '_'))
    }
    case 'sandbox': {
      // Mirror setAppMode's continuity guard: entering sandbox FROM demo with a
      // company ridden clears the stale sandbox org, so nothing can match.
      const effectiveOrg = appMode === 'demo' && currentCompanyId ? null : activeOrganizationId
      return !!effectiveOrg && orgId === effectiveOrg
    }
    case 'production':
      return !!activeOrganizationId && orgId === activeOrganizationId
  }
}

const MODE_ICONS: Record<AppMode, React.ReactNode> = {
  demo: <Play size={14} />,
  sandbox: <Box size={14} />,
  development: <Code2 size={14} />,
  quality: <ShieldCheck size={14} />,
  production: <Rocket size={14} />,
}

// Color-coded LED per environment — the pill's "you are here" signal.
const MODE_LED: Record<AppMode, string> = {
  demo: 'bg-blue-500',
  sandbox: 'bg-amber-500',
  development: 'bg-indigo-500',
  quality: 'bg-emerald-500',
  production: 'bg-red-500',
}

const MODE_ORDER: AppMode[] = ['demo', 'sandbox', 'development', 'quality', 'production']

export function ModeSwitcher() {
  const appMode = useAppStore((s) => s.appMode)
  const setAppMode = useAppStore((s) => s.setAppMode)
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const config = APP_MODE_CONFIGS[appMode]
  const restricted = useProjectEntitlementStore((s) => s.projectIds !== null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  function handleSelect(mode: AppMode) {
    if (mode === appMode) {
      setOpen(false)
      return
    }
    // Tunnel: switch the lifecycle IN PLACE when the current page's selection
    // survives the new mode's scope — the page stays mounted and re-renders
    // with the new mode's data. Only bounce to the home workspace when the
    // selection can't carry (e.g. a demo-company page into a sandbox org).
    const stay = selectionSurvives(mode)
    setAppMode(mode, { preserveContext: stay })
    setOpen(false)
    if (!stay) navigate('/')
  }

  // #115 P1: the Mode Switcher is internal cockpit chrome — hide it on a client
  // product build (VITE_SURFACE=product). Default internal → demo unchanged.
  if (!isInternalSurface()) return null
  // A project-restricted user (outside tester) stays in their own account and environment.
  if (restricted) return null

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`h-9 px-3 rounded-full border transition-colors flex items-center gap-2 text-body-sm font-semibold ${config.bgColor} ${config.color}`}
        aria-label="Switch environment"
        title={`Environment: ${config.label}`}
        aria-expanded={open}
      >
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${MODE_LED[appMode]}`} />
        <span className="hidden sm:inline">{config.label}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="fixed inset-x-2 top-14 sm:absolute sm:inset-x-auto sm:right-0 sm:top-11 sm:w-80 max-h-[85vh] overflow-y-auto bg-white rounded-lg shadow-dropdown border border-border py-1 animate-slide-in-up z-50">
          <div className="px-3 py-2 border-b border-border">
            <div className="text-body-sm font-semibold text-text-primary">Application Mode</div>
            <div className="text-[11px] text-text-tertiary">Controls data source, auth, and configuration access</div>
          </div>

          {MODE_ORDER.map((mode) => {
            const mc = APP_MODE_CONFIGS[mode]
            const isActive = mode === appMode
            return (
              <button
                key={mode}
                onClick={() => handleSelect(mode)}
                className={`w-full text-left px-3 py-2.5 flex items-start gap-3 transition-colors ${
                  isActive ? 'bg-surface-muted' : 'hover:bg-surface-muted/50'
                }`}
              >
                <div className={`mt-0.5 flex-shrink-0 w-7 h-7 rounded-md border flex items-center justify-center ${mc.bgColor} ${mc.color}`}>
                  {MODE_ICONS[mode]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-body-sm font-medium ${isActive ? 'text-text-primary' : 'text-text-secondary'}`}>
                      {mc.label}
                    </span>
                    {isActive && <Check size={14} className="text-brand-500" />}
                  </div>
                  <div className="text-[11px] text-text-tertiary leading-tight mt-0.5">
                    {mc.description}
                  </div>
                  <div className="flex items-center gap-3 mt-1.5">
                    {!mc.requiresAuth && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">
                        No login required
                      </span>
                    )}
                    {mc.allowConfigChanges ? (
                      <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                        Config editable
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full">
                        <Lock size={8} /> Config locked
                      </span>
                    )}
                    {mc.allowConfigPromotion && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">
                        <ArrowRightCircle size={8} /> Promote
                      </span>
                    )}
                  </div>
                </div>
              </button>
            )
          })}
          {/* Persona picker + "Preview as a client" moved to the EnvironmentBanner
              (the acting-as strip under the top bar). This dropdown is environments
              only. */}
        </div>
      )}
    </div>
  )
}
